(ns arthur.domain.canon
  "One canonical text for a map, so that hashing it means something.

  A content address is a hash of a DESCRIPTION of every input, and a description
  only addresses anything if the same inputs always write the same bytes. A CLJS
  map has no key order, `pr-str` will happily print `{:a 1 :b 2}` in either order
  between runs, and JSON has no canonical form of its own. So this is the one
  place that decides.

  The text is VALID JSON, deliberately. The server stores it beside the key and
  verifies `sha256(descriptor) == key` (clips/views.py), and it also has to read
  two fields out of it to enforce that a detector version was declared at all.
  Hashing the text the client sent, rather than recomputing it from parsed
  values, is what keeps that check free of a cross-language float-formatting
  agreement nobody could hold: Python writes `1.0` where JS writes `1`, and a
  scheme where both sides re-render the numbers would break on the first integral
  double. The bytes are the contract; the schema on top of them is a convention.

  It is also meant to be READ. A stale bake presents as a picture that will not
  update, and the descriptor is the only thing that can say which input moved, so
  it is short, flat where it can be, and never has a 229-frame mask inlined —
  see `arthur.flow.address`, which digests masks before they reach here.

  Three refusals, all of them cases where a canonical text is not possible or
  the key would be ambiguous:

    A KEYWORD VALUE. Keys are keywords and become their names, because a key is
    a name and nothing else. A keyword VALUE is refused instead of being named,
    because then `:mouth` and \"mouth\" would hash alike, and the server would be
    reading a field whose type depended on the caller's mood. Callers convert at
    the boundary, which is also what makes the stored JSON clean.

    A SET. Unordered, so there is no one text for it. Sort it into a vector at
    the call site, where it is obvious which order was meant.

    NaN OR INFINITY. Neither is JSON, and both mean a measurement went wrong
    upstream of here — silently addressing it would cache the mistake."
  (:require [clojure.string :as str]))

(defn- number->text [x]
  (when-not (js/Number.isFinite x)
    (throw (ex-info "a descriptor cannot hold NaN or infinity" {:value x})))
  ;; `(str 1.0)` is "1" and `(str 0.12)` is "0.12": JS prints the shortest decimal
  ;; that round-trips, so this is stable without a format string.
  (str x))

(defn- key->text [k]
  (cond
    (keyword? k) (subs (str k) 1)          ; :a -> "a", :roto/b -> "roto/b"
    (string? k)  k
    :else (throw (ex-info "a descriptor key is a keyword or a string"
                          {:key k :type (type k)}))))

(declare write)

(defn- write-map [m]
  (str "{"
       (str/join "," (map (fn [[k v]] (str (js/JSON.stringify (key->text k)) ":" (write v)))
                          (sort-by (comp key->text key) (seq m))))
       "}"))

(defn write
  "The canonical JSON text of a descriptor value."
  [v]
  (cond
    (nil? v)     "null"
    (true? v)    "true"
    (false? v)   "false"
    (number? v)  (number->text v)
    (string? v)  (js/JSON.stringify v)
    (map? v)     (write-map v)
    (set? v)     (throw (ex-info "a descriptor cannot hold a set: sort it into a vector where the order is visible"
                                 {:value v}))
    (keyword? v) (throw (ex-info "a descriptor cannot hold a keyword VALUE: name it at the call site, so \"mouth\" and :mouth cannot address the same block"
                                 {:value v}))
    (sequential? v) (str "[" (str/join "," (map write v)) "]")
    :else (throw (ex-info "not a descriptor value" {:value v :type (type v)}))))
